import React, { useState } from 'react'
import {
  Box,
  Button,
  Alert,
  Typography,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material'
import { startUrlVerification } from '../../contentService/utility'
import { FlowIdView } from '../../contentService/JobView'
import { UrlVerificationJob } from '../../contentService/types'
import { UrlSwap } from '../../../domains/content/features/url-swap/UrlSwap'

type VerificationMode = 'find' | 'replace'

/**
 * Broken Links verification component for TreeVerifications.
 * Verifies all URLs in a curriculum tree to find broken links,
 * and provides bulk URL replacement via spreadsheet.
 */
export function BrokenLinksVerification({
  treeId,
  versionId,
}: {
  treeId: string
  versionId: string
}) {
  const [mode, setMode] = useState<VerificationMode>('find')
  const [flowId, setFlowId] = useState<string>()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string>()

  const handleStartVerification = async () => {
    setLoading(true)
    setError(undefined)

    try {
      const newFlowId = await startUrlVerification({
        tree_id: treeId,
        version_id: versionId,
      })
      setFlowId(newFlowId)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setLoading(false)
    }
  }

  const handleReset = () => {
    setFlowId(undefined)
    setError(undefined)
  }

  const handleTabChange = (
    _event: React.SyntheticEvent,
    newValue: VerificationMode,
  ) => {
    setMode(newValue)
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Broken Links Verification
      </Typography>

      {/* Tabs for mode selection */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={mode}
          onChange={handleTabChange}
          aria-label="verification mode tabs"
        >
          <Tab label="Find Broken Links" value="find" />
          <Tab label="Replace URLs from Spreadsheet" value="replace" />
        </Tabs>
      </Box>

      {/* Find Broken Links Mode */}
      {mode === 'find' && (
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Verify all URLs in this tree to find broken links. This will check
            both internal S3 URLs and external URLs.
          </Typography>

          {!flowId && (
            <Box marginTop="20px">
              {error && (
                <Alert severity="error" sx={{ marginBottom: '10px' }}>
                  {error}
                </Alert>
              )}
              <Button
                variant="contained"
                onClick={handleStartVerification}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : null}
              >
                {loading ? 'Starting Verification...' : 'Start Verification'}
              </Button>
            </Box>
          )}

          {flowId && (
            <Box marginTop="20px">
              <Box marginBottom="10px">
                <Button variant="outlined" size="small" onClick={handleReset}>
                  Start New Verification
                </Button>
              </Box>
              <FlowIdView<UrlVerificationJob> flowId={flowId} />
            </Box>
          )}
        </Box>
      )}

      {/* Replace URLs Mode */}
      {mode === 'replace' && (
        <Box>
          <UrlSwap treeId={treeId} versionId={versionId} />
        </Box>
      )}
    </Box>
  )
}
